import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import Select from "react-select";
import dayjs from "dayjs";
import { MdClose, MdOutlineSearch } from "react-icons/md";
import Sidebar from "../Sidebar";
import Layout from "../../layout/Layout";
import { GET_PROFESSIONAL_APP_ACTIVITY } from "./graphql";

// What professionals do in their app: which features get used and by how
// many, what each person did, and the cash each one still has to settle.
// Same log as Office Activity (admin_activity_logs), professional rows only.

const PAGE_SIZE = 50;

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last7", label: "7 days" },
  { key: "last30", label: "30 days" },
  { key: "custom", label: "Custom" },
];

const GROUP_CLS = {
  APP: "bg-gray-100 text-gray-700",
  ATTENDANCE: "bg-teal-50 text-teal-700",
  BOOKING: "bg-sky-50 text-sky-700",
  SERVICE: "bg-indigo-50 text-indigo-700",
  CUSTOMER: "bg-violet-50 text-violet-700",
  PAYMENT: "bg-emerald-50 text-emerald-700",
  INVENTORY: "bg-amber-50 text-amber-700",
  LEAVE: "bg-pink-50 text-pink-700",
  PAY: "bg-lime-50 text-lime-700",
  REWARDS: "bg-yellow-50 text-yellow-700",
  SAFETY: "bg-red-50 text-red-700",
  PROFILE: "bg-gray-100 text-gray-600",
  OTHER: "bg-gray-100 text-gray-600",
};
// Short column heads for the per-person table.
const GROUP_SHORT = {
  APP: "App",
  ATTENDANCE: "Attend.",
  BOOKING: "Booking",
  SERVICE: "Service",
  CUSTOMER: "Customer",
  PAYMENT: "Payment",
  INVENTORY: "Stock",
  LEAVE: "Leave",
  PAY: "My pay",
  REWARDS: "Rewards",
  SAFETY: "SOS",
  PROFILE: "Profile",
  OTHER: "Other",
};

const rupees = (v) => (v === null || v === undefined ? "—" : `₹${Math.round(v).toLocaleString("en-IN")}`);
const when = (iso) => dayjs(iso).format("D MMM, h:mm A");
const ago = (iso) => {
  if (!iso) return "—";
  const days = dayjs().startOf("day").diff(dayjs(iso).startOf("day"), "day");
  if (days === 0) return `Today ${dayjs(iso).format("h:mm A")}`;
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
};

const Card = ({ title, hint, right, children }) => (
  <section className="bg-white rounded-xl border border-gray-100 shadow-sm">
    {(title || right) && (
      <header className="flex flex-wrap items-start justify-between gap-2 px-4 pt-4">
        <div>
          <h2 className="font-semibold text-gray-900">{title}</h2>
          {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
        </div>
        {right}
      </header>
    )}
    <div className="p-4">{children}</div>
  </section>
);

const Kpi = ({ label, value, sub, valueClass = "text-gray-900" }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
    <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
    <p className={`text-3xl font-semibold mt-1 tabular-nums ${valueClass}`}>{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </div>
);

const Toggle = ({ on, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-2.5 py-1 rounded-full text-xs border ${on ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}
  >
    {children}
  </button>
);

const ArgsView = ({ args }) => {
  let parsed = null;
  try {
    parsed = args ? JSON.parse(args) : null;
  } catch (e) {
    return <pre className="text-xs whitespace-pre-wrap">{args}</pre>;
  }
  if (!parsed || !Object.keys(parsed).length) return <p className="text-xs text-gray-400">No details.</p>;
  const flat = [];
  const walk = (obj, prefix) =>
    Object.entries(obj).forEach(([k, v]) => {
      const key = prefix ? `${prefix}.${k}` : k;
      if (v && typeof v === "object" && !Array.isArray(v)) walk(v, key);
      else flat.push([key.replace(/^data\./, ""), Array.isArray(v) ? JSON.stringify(v) : String(v)]);
    });
  walk(parsed, "");
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 text-xs">
      {flat.map(([k, v]) => (
        <React.Fragment key={k}>
          <dt className="text-gray-500">{k}</dt>
          <dd className="text-gray-800 break-all">{v}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
};

const selectStyles = {
  control: (b) => ({ ...b, minHeight: 38, borderColor: "#e5e7eb", boxShadow: "none" }),
  multiValue: (b) => ({ ...b, backgroundColor: "#FBB033" }),
};

const ProfessionalAppActivity = () => {
  const [preset, setPreset] = useState("last7");
  const [custom, setCustom] = useState({ start: dayjs().subtract(6, "day").format("YYYY-MM-DD"), end: dayjs().format("YYYY-MM-DD") });
  const [employeeIds, setEmployeeIds] = useState([]);
  const [groups, setGroups] = useState([]);
  const [features, setFeatures] = useState([]);
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [onlyUnused, setOnlyUnused] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchText.trim()), 400);
    return () => clearTimeout(t);
  }, [searchText]);
  useEffect(() => setPage(0), [preset, custom, employeeIds, groups, features, onlyErrors, search]);

  const range = useMemo(() => {
    const today = dayjs().format("YYYY-MM-DD");
    if (preset === "today") return { startDate: today, endDate: today };
    if (preset === "yesterday") {
      const y = dayjs().subtract(1, "day").format("YYYY-MM-DD");
      return { startDate: y, endDate: y };
    }
    if (preset === "last30") return { startDate: dayjs().subtract(29, "day").format("YYYY-MM-DD"), endDate: today };
    if (preset === "custom") return { startDate: custom.start, endDate: custom.end };
    return { startDate: dayjs().subtract(6, "day").format("YYYY-MM-DD"), endDate: today };
  }, [preset, custom]);

  const { data, loading, error } = useQuery(GET_PROFESSIONAL_APP_ACTIVITY, {
    variables: {
      filter: { ...range, employeeIds, groups, features, onlyErrors, search },
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    },
    fetchPolicy: "cache-and-network",
  });
  const res = data?.getProfessionalAppActivity;
  const d = res?.data;
  const errorMessage = error?.message || (res?.status === "ERROR" && res.message);

  const groupLabel = useMemo(() => Object.fromEntries((d?.groups || []).map((g) => [g.key, g.label])), [d]);
  const people = useMemo(
    () => (d?.people || []).map((p) => ({ ...p, byGroup: (() => { try { return JSON.parse(p.byGroup || "{}"); } catch (e) { return {}; } })() })),
    [d]
  );
  const team = people.filter((p) => p.status !== "NOT ON TEAM");
  const teamSize = team.length || 1;
  // Groups anyone used in the period, for the per-person columns.
  const usedGroups = useMemo(() => (d?.groups || []).map((g) => g.key).filter((k) => people.some((p) => p.byGroup[k])), [d, people]);

  const personOptions = useMemo(
    () => people.map((p) => ({ value: String(p.employeeId), label: p.name || `#${p.employeeId}` })).sort((a, b) => a.label.localeCompare(b.label)),
    [people]
  );

  const kpis = useMemo(() => {
    const fs = d?.features || [];
    return {
      activeOnApp: team.filter((p) => p.actions > 0).length,
      actions: people.reduce((s, p) => s + p.actions, 0),
      errors: people.reduce((s, p) => s + p.errors, 0),
      featuresUsed: fs.filter((f) => f.uses > 0).length,
      featuresTotal: fs.length,
      cash: people.reduce((s, p) => s + p.cashPending, 0),
      cashPeople: people.filter((p) => p.cashPending > 0).length,
      requested: people.reduce((s, p) => s + p.settleRequested, 0),
    };
  }, [d, people, team]);

  const featuresByGroup = useMemo(() => {
    const out = [];
    (d?.groups || []).forEach((g) => {
      const rows = (d?.features || []).filter((f) => f.group === g.key && (!onlyUnused || f.uses === 0));
      if (rows.length) out.push([g, rows]);
    });
    return out;
  }, [d, onlyUnused]);

  const toggleGroup = (k) => setGroups((c) => (c.includes(k) ? c.filter((x) => x !== k) : [...c, k]));
  // After the re-render, or there is nothing to scroll to yet.
  const scrollTo = (id) => setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 50);
  const pickFeature = (k) => {
    setFeatures([k]);
    setGroups([]);
    scrollTo("pa-feed");
  };
  const pickPerson = (id) => {
    setEmployeeIds([String(id)]);
    scrollTo("pa-top");
  };
  const featureLabel = (k) => d?.features.find((f) => f.key === k)?.label || k;
  const pages = d ? Math.ceil(d.total / PAGE_SIZE) : 0;
  const filtersOn = employeeIds.length > 0 || groups.length > 0 || features.length > 0 || search || onlyErrors;

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <Layout>
        <div className="max-w-7xl space-y-5 pb-16">
          <div id="pa-top">
            <h1 className="text-2xl font-semibold text-white">Professional app activity</h1>
            <p className="text-sm text-gray-400 mt-0.5">
              Every button a professional presses in their app that changes something: who did what and when, which features nobody uses, and whose cash is still unsettled.
              {d?.trackingSince ? ` Recorded since ${dayjs(d.trackingSince).format("D MMM YYYY")}.` : " Recording starts once the backend update is live."}
            </p>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 flex flex-wrap items-center gap-3">
            <div className="flex rounded-lg bg-gray-100 p-0.5">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPreset(p.key)}
                  className={`px-3 py-1.5 rounded-md text-sm ${preset === p.key ? "bg-white shadow-sm font-semibold text-gray-900" : "text-gray-600 hover:text-gray-900"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {preset === "custom" && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <input type="date" value={custom.start} max={custom.end} onChange={(e) => e.target.value && setCustom((c) => ({ ...c, start: e.target.value }))} className="border border-gray-200 rounded-lg px-2 py-1.5" />
                to
                <input type="date" value={custom.end} min={custom.start} onChange={(e) => e.target.value && setCustom((c) => ({ ...c, end: e.target.value }))} className="border border-gray-200 rounded-lg px-2 py-1.5" />
              </div>
            )}
            <div className="min-w-[220px] flex-1 max-w-sm">
              <Select
                isMulti
                placeholder="All professionals"
                options={personOptions}
                value={personOptions.filter((o) => employeeIds.includes(o.value))}
                onChange={(sel) => setEmployeeIds(sel ? sel.map((x) => x.value) : [])}
                styles={selectStyles}
              />
            </div>
            <span className="text-xs text-gray-400 ml-auto">
              {dayjs(range.startDate).format("D MMM YYYY")} – {dayjs(range.endDate).format("D MMM YYYY")}
            </span>
          </div>

          {errorMessage && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">Could not load professional app activity: {errorMessage}</div>}
          {loading && !d && <div className="py-16 text-center text-gray-400">Loading…</div>}

          {d && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                <Kpi
                  label="Used the app"
                  value={`${kpis.activeOnApp}/${team.length}`}
                  valueClass={kpis.activeOnApp < team.length ? "text-amber-600" : "text-gray-900"}
                  sub="professionals on the team"
                />
                <Kpi label="Actions" value={kpis.actions.toLocaleString("en-IN")} sub={`over ${d.days.length} ${d.days.length === 1 ? "day" : "days"}`} />
                <Kpi
                  label="Features used"
                  value={`${kpis.featuresUsed}/${kpis.featuresTotal}`}
                  sub={`${kpis.featuresTotal - kpis.featuresUsed} nobody touched`}
                />
                <Kpi label="Failed" value={kpis.errors} valueClass={kpis.errors ? "text-red-600" : "text-gray-900"} sub="the app showed an error" />
                <Kpi
                  label="Cash not settled"
                  value={rupees(kpis.cash)}
                  valueClass={kpis.cash ? "text-amber-600" : "text-gray-900"}
                  sub={`with ${kpis.cashPeople} ${kpis.cashPeople === 1 ? "professional" : "professionals"}, all time`}
                />
                <Kpi label="Asked to settle" value={kpis.requested} sub="bookings waiting for the office" />
              </div>

              <Card
                title="By professional"
                hint="Counts are for the period; cash is everything still unsettled. Click a name to see only their actions."
              >
                <div className="overflow-x-auto -mx-4">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                        {["Professional", "Last active", "Days", "Actions", "Opens", ...usedGroups.map((g) => GROUP_SHORT[g] || g), "Failed", "Cash pending", "Oldest", "Asked"].map((h) => (
                          <th key={h} className="px-3 py-2 font-medium whitespace-nowrap first:pl-4">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {people.map((p) => {
                        const idle = p.actions === 0 && p.status !== "NOT ON TEAM";
                        const oldDays = p.oldestUnsettled ? dayjs().diff(dayjs(p.oldestUnsettled), "day") : 0;
                        return (
                          <tr key={p.employeeId} className={`border-b border-gray-50 hover:bg-gray-50 tabular-nums ${idle ? "bg-red-50/40" : ""}`}>
                            <td className="px-3 pl-4 py-2 whitespace-nowrap">
                              <button type="button" onClick={() => pickPerson(p.employeeId)} className="font-medium text-gray-900 hover:underline">
                                {p.name || `#${p.employeeId}`}
                              </button>
                              {p.status && p.status !== "ACTIVE" && <span className="ml-2 text-[11px] text-gray-400">{p.status.toLowerCase()}</span>}
                              {idle && <span className="ml-2 px-1.5 py-0.5 rounded text-[11px] bg-red-50 text-red-700">No app use</span>}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-600">{ago(p.lastAt)}</td>
                            <td className="px-3 py-2">{p.activeDays || <span className="text-gray-300">0</span>}</td>
                            <td className="px-3 py-2 font-medium">{p.actions || <span className="text-gray-300">0</span>}</td>
                            <td className="px-3 py-2">{p.appOpens || <span className="text-gray-300">0</span>}</td>
                            {usedGroups.map((g) => (
                              <td key={g} className="px-3 py-2">{p.byGroup[g] || <span className="text-gray-300">·</span>}</td>
                            ))}
                            <td className={`px-3 py-2 ${p.errors ? "text-red-600" : "text-gray-300"}`}>{p.errors}</td>
                            <td className={`px-3 py-2 whitespace-nowrap ${p.cashPending ? "text-amber-700 font-medium" : "text-gray-300"}`}>
                              {p.cashPending ? `${rupees(p.cashPending)} · ${p.cashPendingBookings}` : "—"}
                            </td>
                            <td className={`px-3 py-2 whitespace-nowrap ${oldDays > 3 ? "text-red-600 font-medium" : "text-gray-600"}`}>
                              {p.oldestUnsettled ? `${oldDays}d` : "—"}
                            </td>
                            <td className="px-3 py-2">{p.settleRequested || <span className="text-gray-300">0</span>}</td>
                          </tr>
                        );
                      })}
                      {!people.length && (
                        <tr>
                          <td colSpan={9} className="px-4 py-8 text-center text-gray-400">No professionals.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>

              <Card
                title="Features"
                hint={`Every action in the professional app, with how many of the ${team.length} professionals used it. Click one to see who.`}
                right={
                  <Toggle on={onlyUnused} onClick={() => setOnlyUnused((v) => !v)}>
                    Only unused
                  </Toggle>
                }
              >
                <div className="grid gap-x-8 gap-y-5 lg:grid-cols-2">
                  {featuresByGroup.map(([g, rows]) => (
                    <div key={g.key}>
                      <p className={`inline-block px-1.5 py-0.5 rounded text-[11px] mb-1.5 ${GROUP_CLS[g.key] || GROUP_CLS.OTHER}`}>{g.label}</p>
                      <div className="divide-y divide-gray-50">
                        {rows.map((f) => {
                          const pct = Math.min(100, Math.round((f.people / teamSize) * 100));
                          return (
                            <button key={f.key} type="button" onClick={() => pickFeature(f.key)} className="w-full text-left py-1.5 flex items-center gap-3 text-sm hover:bg-gray-50 rounded">
                              <span className={`flex-1 min-w-0 truncate ${f.uses ? "text-gray-800" : "text-gray-400"}`}>{f.label}</span>
                              {f.uses ? (
                                <>
                                  <span className="w-20 h-1.5 rounded-full bg-gray-100 overflow-hidden shrink-0" title={`${f.people} of ${team.length} professionals`}>
                                    <span className="block h-full bg-[#FBB033]" style={{ width: `${pct}%` }} />
                                  </span>
                                  <span className="w-14 shrink-0 text-xs text-gray-600 tabular-nums">{f.people} ppl</span>
                                  <span className="w-12 shrink-0 text-xs text-gray-900 tabular-nums text-right">{f.uses.toLocaleString("en-IN")}</span>
                                  <span className={`w-10 shrink-0 text-xs tabular-nums text-right ${f.errors ? "text-red-600" : "text-gray-300"}`}>{f.errors ? `${f.errors}✕` : ""}</span>
                                </>
                              ) : (
                                <span className="text-xs text-gray-400 shrink-0">Nobody used</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                <p className={`mt-4 text-xs ${d.notTracked.length ? "text-amber-700" : "text-gray-400"}`}>
                  {d.notTracked.length
                    ? `Not tracked yet (add to professional-app-activity.service.js): ${d.notTracked.join(", ")}`
                    : "Every professional-app action that changes something is tracked. Screens they only look at (My pay, leaderboard) are not."}
                </p>
              </Card>

              <div id="pa-feed">
                <Card
                  title="Every action"
                  hint={`${d.total} matching, newest first. Click a row for the details.`}
                  right={
                    <div className="relative">
                      <MdOutlineSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        placeholder="Booking id, name…"
                        className="pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm w-64"
                      />
                    </div>
                  }
                >
                  <div className="flex flex-wrap gap-2 mb-3">
                    {d.groups.map((g) => {
                      const n = people.reduce((s, p) => s + (p.byGroup[g.key] || 0), 0);
                      if (!n && !groups.includes(g.key)) return null;
                      return (
                        <Toggle key={g.key} on={groups.includes(g.key)} onClick={() => toggleGroup(g.key)}>
                          {g.label} · {n}
                        </Toggle>
                      );
                    })}
                    <span className="w-px bg-gray-200 mx-1" />
                    <Toggle on={onlyErrors} onClick={() => setOnlyErrors((v) => !v)}>Failed</Toggle>
                    {features.map((k) => (
                      <span key={k} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-gray-900 text-white">
                        {featureLabel(k)}
                        <button type="button" onClick={() => setFeatures([])} aria-label="Remove">
                          <MdClose />
                        </button>
                      </span>
                    ))}
                    {filtersOn && (
                      <button
                        type="button"
                        onClick={() => {
                          setEmployeeIds([]);
                          setGroups([]);
                          setFeatures([]);
                          setOnlyErrors(false);
                          setSearchText("");
                        }}
                        className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900 ml-1"
                      >
                        <MdClose /> Clear
                      </button>
                    )}
                  </div>

                  <div className="divide-y divide-gray-100 -mx-4">
                    {d.entries.map((e) => {
                      const isOpen = open === e.id;
                      return (
                        <div key={e.id} className={e.outcome === "ERROR" ? "bg-red-50/30" : ""}>
                          <button type="button" onClick={() => setOpen(isOpen ? null : e.id)} className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                            <span className="w-32 shrink-0 text-gray-500 tabular-nums">{when(e.createdAt)}</span>
                            <span className="w-32 shrink-0 font-medium text-gray-900 truncate">{e.employeeName || "Professional"}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[11px] ${GROUP_CLS[e.group] || GROUP_CLS.OTHER}`}>{groupLabel[e.group] || e.group}</span>
                            <span className="text-gray-800">{e.label}</span>
                            {e.bookingId && <span className="font-mono text-xs text-gray-600">{e.bookingId}</span>}
                            {e.amount !== null && <span className="font-semibold text-gray-900">{e.feature === "redeemRewardCoins" ? `${e.amount} coins` : rupees(e.amount)}</span>}
                            {e.outcome === "ERROR" && <span className="ml-auto px-1.5 py-0.5 rounded text-[11px] bg-red-50 text-red-700">Failed</span>}
                          </button>
                          {isOpen && (
                            <div className="px-4 pb-3 pl-[9.5rem] space-y-2">
                              {e.message && <p className={`text-xs ${e.outcome === "ERROR" ? "text-red-700" : "text-gray-600"}`}>{e.message}</p>}
                              <ArgsView args={e.args} />
                              <p className="text-[11px] text-gray-400">{e.feature}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {!d.entries.length && <p className="px-4 py-8 text-center text-gray-400 text-sm">Nothing matches these filters.</p>}
                  </div>

                  {pages > 1 && (
                    <div className="flex items-center justify-between mt-3 text-sm">
                      <button type="button" disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40">
                        Newer
                      </button>
                      <span className="text-gray-500">
                        Page {page + 1} of {pages}
                      </span>
                      <button type="button" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40">
                        Older
                      </button>
                    </div>
                  )}
                </Card>
              </div>
            </>
          )}
        </div>
      </Layout>
    </div>
  );
};

export default ProfessionalAppActivity;
